import { inject, Injectable } from '@angular/core';
import { forkJoin, map, of, switchMap } from 'rxjs';
import type { IGridRequestBody } from 'mt-ui-lib/interfaces';
import type {
  ISystemParam,
  ISystemParamsGridItem,
  ISystemParamUpdateParams,
} from '../interfaces/system-params';
import { IamPoliciesApiService } from './iam-policies-api.service';
import type {
  RealmPolicies,
  AuthenticationExecution,
  EmailCodePolicy,
} from './iam-policies-api.service';
import { KeycloakSessionService } from '../services/auth/keycloak-session.service';
import { RUNTIME_CONFIG } from '../config/runtime-config';
import { POLICY_SECTIONS } from './policy-fields';
import {
  PASSWORD_FIELDS,
  readPasswordPolicy,
  updatePasswordPolicy,
} from './password-policy';
import { settingsGridPage } from './grid-query';
import type { SystemParamsDataSource } from './system-params-data-source';
import { booleanSystemParam } from './boolean-system-param';

@Injectable({ providedIn: 'root' })
export class SystemParamsApiService implements SystemParamsDataSource {
  public readonly source = 'iam';
  private readonly api = inject(IamPoliciesApiService);
  private readonly session = inject(KeycloakSessionService);
  private readonly config = inject(RUNTIME_CONFIG);
  private rows() {
    return forkJoin({
      realm: this.api.realm(),
      ip: this.api.ipPolicy(),
      temporary: this.api.temporaryPasswordPolicy(),
      executions: this.api.executions(),
    }).pipe(
      switchMap(({ realm, ip, temporary, executions }) => {
        const execution = executions.find(
          (item) => item.providerId === 'mt-email-otp',
        );
        return (
          execution?.authenticationConfig
            ? this.api.emailCodePolicy(execution.authenticationConfig)
            : of(null)
        ).pipe(
          map((otp) =>
            this.toRows(
              realm,
              ip.defaultAllowedIpNetworks,
              temporary.lifespanSeconds,
              execution,
              otp,
            ),
          ),
        );
      }),
    );
  }
  private toRows(
    realm: RealmPolicies,
    defaultAllowedIpNetworks: string[],
    lifespanSeconds: number,
    execution: AuthenticationExecution | undefined,
    otp: EmailCodePolicy | null,
  ): ISystemParam[] {
    const password = readPasswordPolicy(realm.passwordPolicy);
    const row = (
      id: string,
      name: string,
      value: unknown,
      description = '',
      minValue: number = null,
      maxValue: number = null,
      protectedValue = false,
    ): ISystemParam => ({
      id,
      name,
      value: value == null ? '' : String(value),
      description,
      minValue,
      maxValue,
      inputMask: '.',
      regexpMask: '',
      isProtected: protectedValue || !this.session.isSecurityAdministrator,
    });
    return [
      ...PASSWORD_FIELDS.map((field) =>
        row(
          'password:' + field.key,
          field.label,
          password[field.key],
          'Политика пароля',
          field.min,
        ),
      ),
      ...POLICY_SECTIONS.flatMap((section) =>
        section.fields.map((field) =>
          field.type === 'checkbox'
            ? booleanSystemParam(
                {
                  id: 'realm:' + field.key,
                  name: field.label,
                  description: section.description,
                  isProtected: !this.session.isSecurityAdministrator,
                },
                realm[field.key],
              )
            : row(
                'realm:' + field.key,
                field.label,
                realm[field.key],
                section.description,
                field.min,
                field.max ?? null,
              ),
        ),
      ),
      row(
        'ip',
        'Разрешённые IP-адреса по умолчанию',
        defaultAllowedIpNetworks.join(', '),
        'Адреса и CIDR через запятую; пустое значение снимает общее ограничение',
      ),
      row(
        'temporary',
        'Срок временного пароля',
        lifespanSeconds,
        'Секунды',
        1,
        2147483647,
      ),
      booleanSystemParam(
        {
          id: 'mfa',
          name: 'Двухфакторная аутентификация по email',
          description: 'Требовать код из письма при входе по паролю.',
          isProtected: !this.session.isSecurityAdministrator,
        },
        execution?.requirement === 'REQUIRED'
          ? true
          : execution?.requirement === 'DISABLED'
            ? false
            : null,
      ),
      ...[
        ['ttlSeconds', 'Срок кода подтверждения, секунд', 60, 900],
        ['maxAttempts', 'Число попыток ввода кода', 1, 10],
        [
          'resendCooldownSeconds',
          'Пауза повторной отправки кода, секунд',
          30,
          300,
        ],
        ['maxSends', 'Число отправок кода', 1, 10],
      ].map(([key, label, min, max]) =>
        row(
          'otp:' + key,
          String(label),
          otp?.config[String(key)],
          '',
          Number(min),
          Number(max),
          !otp,
        ),
      ),
      row(
        'supportEmail',
        'Электронная почта поддержки',
        this.config.supportEmail,
        'Настройка окружения',
        null,
        null,
        true,
      ),
      row(
        'supportPhone',
        'Телефон поддержки',
        this.config.supportPhone,
        'Настройка окружения',
        null,
        null,
        true,
      ),
    ];
  }
  getGrid(body: IGridRequestBody) {
    return this.rows().pipe(
      map((rows) =>
        settingsGridPage(
          rows.map<ISystemParamsGridItem>((row) => ({
            id: row.id,
            name: row.name,
            description: row.description,
            value: row.listValue ?? row.value,
          })),
          body,
        ),
      ),
    );
  }
  getInfo(id: string) {
    return this.rows().pipe(
      map((rows) => {
        const row = rows.find((item) => item.id === id);
        if (!row) throw new Error('Параметр не найден');
        return row;
      }),
    );
  }
  updateParam(id: string, params: ISystemParamUpdateParams) {
    return this.getInfo(id).pipe(
      switchMap((row) => {
        if (row.isProtected)
          throw new Error('Параметр доступен только для чтения');
        const value = params.value.trim();
        if (id === 'ip') {
          return this.api.updateIpPolicy(
            value
              .split(/[,\n]/)
              .map((x) => x.trim())
              .filter(Boolean),
          );
        }
        const [source, key] = id.split(':');
        if (id === 'mfa') {
          if (!['true', 'false'].includes(value))
            throw new Error('Задайте true или false');
          return this.emailExecution().pipe(
            switchMap((execution) =>
              this.api.updateExecution(execution, value === 'true'),
            ),
          );
        }
        const field = POLICY_SECTIONS.flatMap((section) => section.fields).find(
          (item) => item.key === key,
        );
        if (source === 'realm' && field?.type === 'checkbox') {
          if (!['true', 'false'].includes(value))
            throw new Error('Задайте true или false');
          return this.api.updateRealm({ [key]: value === 'true' });
        }
        const number = Number(value);
        if (
          !value ||
          !Number.isSafeInteger(number) ||
          (row.minValue != null && number < row.minValue) ||
          (row.maxValue != null && number > row.maxValue)
        )
          throw new Error('Некорректное значение параметра');
        if (source === 'realm') {
          if (
            key === 'ssoSessionIdleTimeout' ||
            key === 'ssoSessionMaxLifespan'
          ) {
            return this.api.realm().pipe(
              switchMap((realm) => {
                const idle =
                  key === 'ssoSessionIdleTimeout'
                    ? number
                    : realm.ssoSessionIdleTimeout;
                const maximum =
                  key === 'ssoSessionMaxLifespan'
                    ? number
                    : realm.ssoSessionMaxLifespan;
                if (idle > maximum)
                  throw new Error(
                    'Допустимое бездействие SSO не может превышать предельный срок SSO',
                  );
                return this.api.updateRealm({ [key]: number });
              }),
            );
          }
          return this.api.updateRealm({ [key]: number });
        } else if (source === 'password') {
          return this.api.realm().pipe(
            switchMap((realm) =>
              this.api.updateRealm({
                passwordPolicy: updatePasswordPolicy(realm.passwordPolicy, {
                  [key]: number,
                }),
              }),
            ),
          );
        } else if (id === 'temporary')
          return this.api.updateTemporaryPasswordPolicy(number);
        else if (source === 'otp') {
          return this.emailExecution().pipe(
            switchMap((execution) => {
              if (!execution.authenticationConfig)
                throw new Error('Настройки кода недоступны');
              return this.api.emailCodePolicy(execution.authenticationConfig);
            }),
            switchMap((policy) =>
              this.api.updateEmailCodePolicy({
                ...policy,
                config: { ...policy.config, [key]: value },
              }),
            ),
          );
        } else throw new Error('Изменение не поддерживается');
      }),
    );
  }

  private emailExecution() {
    return this.api.executions().pipe(
      map((executions) => {
        const execution = executions.find(
          (item) => item.providerId === 'mt-email-otp',
        );
        if (!execution) throw new Error('Способ входа недоступен');
        return execution;
      }),
    );
  }
}
